import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import type {
	NumericTextBoxChangeEvent,
	NumericTextBoxFocusEvent,
} from "@tql/ui-components-react/kendo/inputs";
import { NumericTextBox } from "@tql/ui-components-react/tql/inputs/beta";
import { memo, useCallback, useState } from "react";

import type { Invoice } from "~/shared/schemas/invoice-schema";

import { PAYMENT_CONSTRAINTS } from "../../utils/constants.ts";

type PaymentAmountCellProps = Omit<GridCustomCellProps, "dataItem"> & {
	onAmountChange: (amount: number) => void;
	dataItem: Invoice;
};

export const PaymentAmountCell = memo(
	({ onAmountChange, dataItem }: PaymentAmountCellProps) => {
		const [localValue, setLocalValue] = useState<number | null>(
			dataItem.balanceAmt ?? 0,
		);

		const handleChange = useCallback(
			(event: NumericTextBoxChangeEvent) => {
				const value = event.target.value;
				// Only allow positive numbers or zero
				const positiveValue = value !== null && value < 0 ? 0 : value;
				setLocalValue(positiveValue);

				const finalValue = positiveValue ?? 0;
				onAmountChange(finalValue);
			},
			[onAmountChange],
		);

		const handleFocus = useCallback((event: NumericTextBoxFocusEvent) => {
			// Select all text on focus so next keystroke overwrites
			setTimeout(() => {
				const inputElement = event.target.element;
				if (inputElement) {
					inputElement.select();
				}
			}, 0);
		}, []);

		const handleBlur = useCallback(() => {
			// If the field is empty, negative, or zero on blur, reset to 0
			if (localValue === null || localValue <= 0) {
				setLocalValue(0);
				onAmountChange(0);
			}
		}, [localValue, onAmountChange]);

		return (
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: "0.5rem",
				}}
			>
				$
				<NumericTextBox
					format={"n2"}
					max={PAYMENT_CONSTRAINTS.MAX_AMOUNT}
					min={0}
					onBlur={handleBlur}
					onChange={handleChange}
					onFocus={handleFocus}
					spinners={false}
					value={localValue}
				/>
			</div>
		);
	},
);

PaymentAmountCell.displayName = "PaymentAmountCell";
